// ==========================================
// STEP 9: Create hooks/useFriends.js
// ==========================================
import { useState, useCallback } from 'react';
import { useAPI } from './useAPI';

export const useFriends = () => {
  const { apiCall } = useAPI();
  const [friends, setFriends] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);

  const loadFriends = useCallback(async () => {
    try {
      const data = await apiCall('/friends');
      setFriends(data);
    } catch (err) {
      console.error('Error loading friends:', err);
    }
  }, [apiCall]);

  const loadReceivedRequests = useCallback(async () => {
    try {
      const data = await apiCall('/friends/requests/received');
      setReceivedRequests(data);
    } catch (err) {
      console.error('Error loading requests:', err);
    }
  }, [apiCall]);

  const loadSentRequests = useCallback(async () => {
    try {
      const data = await apiCall('/friends/requests/sent');
      setSentRequests(data);
    } catch (err) {
      console.error('Error loading sent requests:', err);
    }
  }, [apiCall]);

  const sendRequest = useCallback(async (friendId) => {
    try {
      await apiCall('/friends/request', {
        method: 'POST',
        body: JSON.stringify({ friend_id: friendId }),
      });
      await loadSentRequests();
    } catch (err) {
      throw err;
    }
  }, [apiCall, loadSentRequests]);

  const acceptRequest = useCallback(async (requestId) => {
    try {
      await apiCall(`/friends/accept/${requestId}`, { method: 'POST' });
      await loadFriends();
      await loadReceivedRequests();
    } catch (err) {
      throw err;
    }
  }, [apiCall, loadFriends, loadReceivedRequests]);

  const rejectRequest = useCallback(async (requestId) => {
    try {
      await apiCall(`/friends/reject/${requestId}`, { method: 'POST' });
      await loadReceivedRequests();
    } catch (err) {
      throw err;
    }
  }, [apiCall, loadReceivedRequests]);

  const removeFriend = useCallback(async (friendId) => {
    try {
      await apiCall(`/friends/${friendId}`, { method: 'DELETE' });
      await loadFriends();
    } catch (err) {
      throw err;
    }
  }, [apiCall, loadFriends]);

  const searchUsers = useCallback(async (query) => {
    try {
      const data = await apiCall(`/users/search?query=${encodeURIComponent(query)}`);
      return data;
    } catch (err) {
      throw err;
    }
  }, [apiCall]);

  return {
    friends,
    receivedRequests,
    sentRequests,
    loadFriends,
    loadReceivedRequests,
    loadSentRequests,
    sendRequest,
    acceptRequest,
    rejectRequest,
    removeFriend,
    searchUsers,
  };
};